import { useAccountState } from '../../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { MouseEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Dropdown,
    Field,
    InfoLabel,
    Input,
    Link,
    makeStyles,
    mergeClasses,
    Option,
    OptionOnSelectData,
    Spinner,
    Text,
    tokens,
} from '@fluentui/react-components';
import {
    AddRegular,
    DeleteRegular,
    DocumentAddRegular,
    Money20Regular,
    SaveRegular,
} from '@fluentui/react-icons';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Column, Paths, Row } from '../../../common';
import {
    Invoice,
    Case,
    CaseExpense,
    Credits,
    TimeTrackingEntry,
    UserAccount,
    AppMessage,
    useInitialValues,
    RedeemedCredits,
} from '../../../../data/types';
import { useToasts } from '../../../toasts/useToasts';
import { useCommonStyles } from '../../../common/Styles/commonStyles';

import { useCreditsService } from '../../../../data/services/useCreditsService';
import { useUtilities } from '../../../../utilities/useUtilities';
import { AppMessageDisplay, useAppMessageDisplay } from '../../../controls/AppMessageDisplay';
import { useAccess } from '../../../../auth/useAccess';
import { TimeTrackingTabs } from '../CaseTimeTracking/TimeTrackingPage';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { CaseTabs } from '../../Tabs/CaseTabs';
import { useValidation } from '../../../../data/types/validation';
import { PayTerms } from '../CaseEngagementDoc/EngagementEnums';
import { useGuid } from '../../../../utilities/useGuid';
import { InvoiceBillableTimeEntryTable, InvoiceExpensesTable } from './CreateInvoicePage';

type Installment = {
    id: string;
    invoiceNumber: string;
    invoiceDate: Date;
    amount: string;
    paymentTerms: string;
};

enum BillingPeriod {
    custom = 'Custom',
}

const useStyles = makeStyles({
    surface: {
        width: 'min(96vw, 1100px)',
        maxWidth: '1100px',
    },
    content: {
        maxHeight: '70vh',
        overflowY: 'auto',
    },
    creditCard: {
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        borderRadius: tokens.borderRadiusMedium,
        padding: '0.5rem 0.75rem',
        minWidth: '11rem',
        backgroundColor: tokens.colorNeutralBackground2,
    },
    creditLabel: {
        color: tokens.colorNeutralForeground3,
    },
    creditValue: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase400,
    },
    creditInput: {
        maxWidth: '10rem',
    },
});

export type CreateInvoiceDialogProps = {
    open: boolean;
    onOpenChange: (isOpen: boolean) => void;
    onInvoicesCreated?: (invoices: Invoice[]) => void;
};

export const CreateInvoiceDialog = (props: CreateInvoiceDialogProps) => {
    const { open, onOpenChange, onInvoicesCreated } = props;

    const { caseForm, invoiceService, expenseService, timeTrackingService, caseEngagementService } =
        useCaseServicesContext()!;
    const caseObj = caseForm.values as Case;
    const assignedUsersMap = new Map(caseObj.assignedUsers?.map((user) => [user.userId, user]) ?? []);

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);
    const initValues = useInitialValues();
    const toasts = useToasts();
    const utils = useUtilities();
    const messageDisplay = useAppMessageDisplay();
    const navTo = useNavigate();
    const { validateInvoice } = useValidation();
    const guid = useGuid();
    const creditsService = useCreditsService();

    //State
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [billPeriodFilter, setBillPeriodFilter] = useState<string>();
    const [payTermsFilter, setPayTermsFilter] = useState<string>(PayTerms.uponReceipt);
    const [installments, setInstallments] = useState<Installment[]>([]);
    const [billMonths, setBillMonths] = useState<Date[]>([]);
    const [credits, setCredits] = useState<Credits | undefined>();
    const [creditsLoading, setCreditsLoading] = useState<boolean>(false);
    const [caseCreditInput, setCaseCreditInput] = useState<string>('');
    const [nonCaseCreditInput, setNonCaseCreditInput] = useState<string>('');

    //Styles
    const styles = useStyles();
    const commonStyles = useCommonStyles();

    //Invoice number generation
    const reGenerateInvoiceNumber = () => {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 1000)
            .toString()
            .padStart(3, '0');
        return `INV-${year}${month}${day}-${random}`;
    };
    const initialFormValues = (): Invoice => {
        return {
            ...initValues.invoice(caseObj.caseId, caseObj.fixedAmount),
            invoiceNumber: reGenerateInvoiceNumber(),
        };
    };

    //Form
    const invoiceForm = useFormik<Invoice>({
        initialValues: initialFormValues(),
        onSubmit: async (values, { resetForm }) => {
            setIsCreating(true);
            try {
                const createdInvoices: Invoice[] = [];
                let adjustedInvoices: Invoice[] = [];

                if (caseObj?.isFixedFee) {
                    // Create one invoice per installment
                    for (let i = 0; i < installments.length; i++) {
                        const inst = installments[i];
                        const instInvoice: Invoice = {
                            ...values,
                            invoiceId: guid.newGuid(),
                            invoiceNumber: inst.invoiceNumber,
                            caseId: caseObj.caseId,
                            invoiceDate: inst.invoiceDate,
                            dueDate: computeDueDate(inst.invoiceDate, inst.paymentTerms),
                            billableTimeEntries: [],
                            expenses: [],
                            fixedAmount: inst.amount,
                            paymentBy: inst.paymentTerms,
                        };
                        createdInvoices.push(instInvoice);
                    }

                    adjustedInvoices = await applyCreditsAsync(createdInvoices);
                    await Promise.all(
                        adjustedInvoices.map((instInvoice, i) =>
                            invoiceService.upsertInvoiceAsync(instInvoice, i === installments.length - 1),
                        ),
                    );

                } else {
                    const newInvoice: Invoice = {
                        ...values,
                        caseId: caseObj.caseId,
                        billableTimeEntries: fillWithRates(
                            timeTrackingService?.billableTimeEntries?.filter(filterTimeEntries),
                        ),
                        expenses: primaryAccount?.account.features?.expenseManagement
                            ? expenseService?.expenses?.filter(filterExpenses)
                            : [],
                        fixedAmount: undefined,
                        paymentBy: payTermsFilter,
                    };
                    createdInvoices.push(newInvoice);

                    adjustedInvoices = await applyCreditsAsync(createdInvoices);
                    await invoiceService.upsertInvoiceAsync(adjustedInvoices[0], true);
                }
                if (onInvoicesCreated && adjustedInvoices.length > 0) {
                    onInvoicesCreated(adjustedInvoices);
                }

                // Reset and close
                resetForm();
                setInstallments([]);
                setCaseCreditInput('');
                setNonCaseCreditInput('');
                onOpenChange(false);
            } catch (error) {
                toasts.notifyError(
                    'Error Creating Invoice',
                    `An error occurred while creating the invoice. ${error}`,
                );
            } finally {
                setIsCreating(false);
            }
        },
        validationSchema: validateInvoice.schema,
    });

    //Access
    const installmentsTotal = () => {
        return installments.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
    };
    const installmentsMatchFixedFee = () => {
        const fixed = Number(caseObj?.fixedAmount) || 0;
        if (fixed <= 0) return false;
        return Math.abs(installmentsTotal() - fixed) < 0.005;
    };
    const canCreateInvoice = () => {
        const goodToGo = !isCreating && invoiceForm.isValid && !!caseObj?.caseId;

        if (caseObj?.isFixedFee) {
            return (
                goodToGo &&
                !!caseObj?.fixedAmount &&
                installments.length > 0 &&
                installments.every(
                    (i) => !!i.invoiceNumber && !!i.invoiceDate && Number(i.amount) > 0 && !!i.paymentTerms,
                ) &&
                installmentsMatchFixedFee()
            );
        } else {
            return (
                goodToGo &&
                hasBillableRates() &&
                !!invoiceForm.values.billingPeriodStart &&
                !!invoiceForm.values.billingPeriodEnd
            );
        }
    };
    const canSeeExpenses = () => {
        return primaryAccount?.account.features?.expenseManagement;
    };

    //Filters
    const filterExpenses = (expense: CaseExpense) => {
        return (
            invoiceForm.values.billingPeriodStart &&
            invoiceForm.values.billingPeriodEnd &&
            new Date(expense.date || 0) >= invoiceForm.values.billingPeriodStart &&
            new Date(expense.date || 0) <= invoiceForm.values.billingPeriodEnd &&
            !expense.invoiceId &&
            !!expense.recoverable
        );
    };
    const filterTimeEntries = (entry: TimeTrackingEntry) => {
        return (
            invoiceForm.values.billingPeriodStart &&
            invoiceForm.values.billingPeriodEnd &&
            new Date(entry.date || 0) >= invoiceForm.values.billingPeriodStart &&
            new Date(entry.date || 0) <= invoiceForm.values.billingPeriodEnd &&
            !entry.invoiceId
        );
    };

    //Credits
    const accountSettings = primaryAccount?.account?.settings;
    const alwaysApplyAllCredits = !!accountSettings?.alwaysApplyAllAvailableCredits;
    const alwaysApplyCaseCredits = !!accountSettings?.alwaysApplyCaseAvailableCredits;
    const clientPartyId = caseObj?.retainedBy?.party?.partyId;

    const invoiceTotal = () => {
        if (caseObj?.isFixedFee) return installmentsTotal();

        const timeTotal = (timeTrackingService?.billableTimeEntries?.filter(filterTimeEntries) ?? []).reduce(
            (sum, entry) =>
                sum + Number(entry.hours || 0) * Number(assignedUsersMap.get(entry.userId!)?.clientBillRate || 0),
            0,
        );
        const expenseTotal = canSeeExpenses()
            ? (expenseService?.expenses?.filter(filterExpenses) ?? []).reduce(
                  (sum, expense) => sum + Number(expense.amount || 0),
                  0,
              )
            : 0;

        return timeTotal + expenseTotal;
    };
    const availableCredit = (caseSpecific: boolean) => {
        const isMatch = (creditCaseId?: string) =>
            caseSpecific ? creditCaseId === caseObj?.caseId : !creditCaseId;
        const issued = (credits?.creditsList ?? []).reduce(
            (sum, credit) => sum + (isMatch(credit.caseId) ? (credit.amount ?? 0) : 0),
            0,
        );
        const redeemed = (credits?.redeemedCreditsList ?? []).reduce(
            (sum, redemption) => sum + (isMatch(redemption.caseId) ? (redemption.amount ?? 0) : 0),
            0,
        );
        return Math.max(0, issued - redeemed);
    };
    const caseCreditAvailable = () => availableCredit(true);
    const nonCaseCreditAvailable = () => availableCredit(false);
    const totalCreditAvailable = () => caseCreditAvailable() + nonCaseCreditAvailable();
    const clampCredit = (value: number, max: number) => Math.max(0, Math.min(value, Math.max(0, max)));
    const maxCaseCredit = () => Math.min(caseCreditAvailable(), invoiceTotal());
    const maxNonCaseCredit = () => Math.min(nonCaseCreditAvailable(), invoiceTotal() - appliedCaseCredit());
    const appliedCaseCredit = () => {
        const max = maxCaseCredit();
        if (alwaysApplyAllCredits || alwaysApplyCaseCredits) return Math.max(0, max);
        return clampCredit(Number(caseCreditInput) || 0, max);
    };
    const appliedNonCaseCredit = () => {
        const max = maxNonCaseCredit();
        if (alwaysApplyAllCredits) return Math.max(0, max);
        return clampCredit(Number(nonCaseCreditInput) || 0, max);
    };
    const totalCreditApplied = () => appliedCaseCredit() + appliedNonCaseCredit();
    const balanceDue = () => Math.max(0, invoiceTotal() - totalCreditApplied());
    const isCaseCreditLocked = () => alwaysApplyAllCredits || alwaysApplyCaseCredits;
    const isNonCaseCreditLocked = () => alwaysApplyAllCredits;
    const showCredits = () => creditsLoading || totalCreditAvailable() > 0;

    const amountForInvoice = (invoice: Invoice) => {
        if (invoice.fixedAmount) return Number(invoice.fixedAmount) || 0;

        const timeTotal = (invoice.billableTimeEntries ?? []).reduce(
            (sum, entry) => sum + Number(entry.hours || 0) * Number(entry.rate || 0),
            0,
        );
        const expenseTotal = (invoice.expenses ?? []).reduce(
            (sum, expense) => sum + Number(expense.amount || 0),
            0,
        );
        return timeTotal + expenseTotal;
    };
    const redeemCreditAsync = async (invoice: Invoice, amount: number, caseSpecific: boolean) => {
        const redeemedCredit: RedeemedCredits = {
            redemptionId: guid.newGuid(),
            caseId: caseSpecific ? caseObj.caseId : undefined,
            invoiceId: invoice.invoiceId,
            description: `Applied to invoice ${invoice.invoiceNumber}`,
            amount,
            dateRedeemed: new Date().toISOString(),
        };

        await creditsService.redeemCreditsAsync(clientPartyId as string, redeemedCredit);

        return redeemedCredit;
    };
    //Credits are consumed case-specific first, then non-case-specific, spread across the created invoices in order
    const applyCreditsAsync = async (invoices: Invoice[]): Promise<Invoice[]> => {
        let caseRemaining = appliedCaseCredit();
        let nonCaseRemaining = appliedNonCaseCredit();

        if (!clientPartyId || caseRemaining + nonCaseRemaining <= 0) return invoices;

        try {
            const adjustedInvoices: Invoice[] = [];
            for (const invoice of invoices) {
                const newInvoice = {...invoice};

                if (caseRemaining + nonCaseRemaining <= 0) break;

                let invoiceRemaining = amountForInvoice(newInvoice);

                const caseAmount = Math.min(caseRemaining, invoiceRemaining);
                if (caseAmount > 0) {
                    const redeemedCaseCredit = await redeemCreditAsync(newInvoice, caseAmount, true);
                    newInvoice.credits = [...newInvoice.credits ?? [], redeemedCaseCredit];
                    caseRemaining -= caseAmount;
                    invoiceRemaining -= caseAmount;
                }

                const nonCaseAmount = Math.min(nonCaseRemaining, invoiceRemaining);
                if (nonCaseAmount > 0) {
                    const redeemedClientCredit = await redeemCreditAsync(newInvoice, nonCaseAmount, false);
                    newInvoice.credits = [...newInvoice.credits ?? [], redeemedClientCredit];
                    nonCaseRemaining -= nonCaseAmount;
                }
                adjustedInvoices.push(newInvoice);
            }

            return adjustedInvoices;
        } catch (error) {
            toasts.notifyError(
                'Error Applying Credits',
                `The invoice was created, but the credits could not be applied. ${error}`,
            );
            return [];
        }
    };
    const loadCreditsAsync = async () => {
        if (!clientPartyId) {
            setCredits(undefined);
            return;
        }

        setCreditsLoading(true);
        try {
            setCredits(await creditsService.getCreditsForPartyAsync(clientPartyId));
        } catch (error) {
            console.error('Load case credits failed', error);
            setCredits(undefined);
        } finally {
            setCreditsLoading(false);
        }
    };

    //Messages
    const onBillableRatesLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        goToBillableRates();
    };
    const getRatesActionText = () => {
        if (access.timeTracking.canEditBillableRates(caseObj.caseId)) {
            return (
                <Text>
                    Please{' '}
                    <Link onClick={onBillableRatesLinkClick} data-testid='create-invoice-dialog-billable-rates-link'>
                        click here
                    </Link>{' '}
                    to enter billable rates for this case.
                </Text>
            );
        } else {
            return <Text>Please contact an admin to enter billable rates for this case.</Text>;
        }
    };
    const noBillableRatesMsg: AppMessage = {
        id: 'noBillableRatesMsg',
        intent: 'warning',
        title: 'Missing Billable Rates',
        content: (
            <Text>
                Your Scope of Work indicates you are billing by the hour. However, not all billable rates have been set.{' '}
                {getRatesActionText()}
            </Text>
        ),
        canDismiss: false,
    };

    //Logic
    const fillWithRates = (timeEntries: TimeTrackingEntry[]) => {
        return timeEntries.map((entry: TimeTrackingEntry) => {
            return {
                ...entry,
                rate: assignedUsersMap.get(entry.userId!)?.clientBillRate,
            } as TimeTrackingEntry;
        });
    };
    const goToBillableRates = () => {
        onOpenChange(false);
        navTo(Paths.viewEditCase(caseObj.caseId as string), {
            state: { tab: CaseTabs.caseTimeTracking, subTab: TimeTrackingTabs.billableRates },
        });
    };
    const hasBillableRates = () => {
        return caseObj.assignedUsers?.every((aUser) => !!aUser.clientBillRate);
    };
    const checkBillableRates = () => {
        if (!caseObj?.isFixedFee && !hasBillableRates()) {
            messageDisplay.addMessage(noBillableRatesMsg);
        }
    };

    const handleGenerateNumber = () => {
        const invNumber = reGenerateInvoiceNumber();
        invoiceForm.setFieldValue('invoiceNumber', invNumber);
        invoiceForm.validateField('invoiceNumber');
    };
    const setDueDate = (payTerms: string) => {
        if (!!invoiceForm.values.invoiceDate) {
            const due = computeDueDate(invoiceForm.values.invoiceDate, payTerms);
            invoiceForm.setFieldValue('dueDate', due);
        }
    };
    const computeDueDate = (invoiceDate: Date, payTerms: string): Date => {
        const invDate = new Date(invoiceDate);
        const dueDate = new Date(invDate);
        if (payTerms == PayTerms.net15) dueDate.setDate(dueDate.getDate() + 15);
        else if (payTerms == PayTerms.net30) dueDate.setDate(dueDate.getDate() + 30);
        else if (payTerms == PayTerms.net60) dueDate.setDate(dueDate.getDate() + 60);
        else if (payTerms == PayTerms.net90) dueDate.setDate(dueDate.getDate() + 90);
        // uponReceipt / custom: same day as invoice date
        return dueDate;
    };

    //Installments
    const addInstallment = () => {
        const remaining = Math.max(0, (Number(caseObj?.fixedAmount) || 0) - installmentsTotal());
        const nextDate = installments.length
            ? new Date(installments[installments.length - 1].invoiceDate)
            : new Date();
        setInstallments([
            ...installments,
            {
                id: guid.newGuid(),
                invoiceNumber: reGenerateInvoiceNumber(),
                invoiceDate: nextDate,
                amount: remaining > 0 ? remaining.toFixed(2) : '',
                paymentTerms: PayTerms.uponReceipt,
            },
        ]);
    };
    const updateInstallment = (id: string, patch: Partial<Installment>) => {
        setInstallments((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
    };
    const removeInstallment = (id: string) => {
        setInstallments((prev) => prev.filter((i) => i.id !== id));
    };
    const regenerateInstallmentNumber = (id: string) => {
        updateInstallment(id, { invoiceNumber: reGenerateInvoiceNumber() });
    };

    //UI Events
    const onDialogOpenChange = (_: unknown, data: { open: boolean }) => {
        if (!data.open && isCreating) return;
        onOpenChange(data.open);
    };
    const onPayTermsChange = (e: any, data: OptionOnSelectData) => {
        e?.stopPropagation?.();
        const payTerms = data.optionText ?? '';
        setPayTermsFilter(payTerms);
        setDueDate(payTerms);
    };
    const onCreateInvoiceClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        invoiceForm.submitForm();
    };
    const onCancelClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onOpenChange(false);
    };
    const onReGenerateInvoiceNumberClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        handleGenerateNumber();
    };
    const onInvoiceDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('invoiceDate', date || new Date());
    };
    const onInvoiceDateTodayClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        invoiceForm.setFieldValue('invoiceDate', new Date());
    };
    const onBillPeriodOptionSelect = (e: any, data: OptionOnSelectData) => {
        e?.stopPropagation?.();
        setBillPeriodFilter(data.optionText ?? '');
    };
    const onBillingPeriodStartDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('billingPeriodStart', date || new Date());
    };
    const onBillingPeriodEndDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('billingPeriodEnd', date || new Date());
    };
    const onDueDateSelect = (date?: Date | null) => {
        invoiceForm.setFieldValue('dueDate', date || new Date());
    };
    const onDueDateClick = (e: MouseEvent<HTMLElement>) => {
        if (caseEngagementService?.engagementDoc?.paymentBy != PayTerms.custom) {
            e.preventDefault();
            e.stopPropagation();
        }
    };
    const onAttachEngagementChange = (e: any, data: any) => {
        e?.stopPropagation?.();
        invoiceForm.setFieldValue('attachEngagement', data.checked);
    };
    const onCaseCreditChange = (_: unknown, data: { value: string }) => {
        const max = maxCaseCredit();
        const value = Number(data.value);
        setCaseCreditInput(!isNaN(value) && value > max ? String(max) : data.value);
    };
    const onNonCaseCreditChange = (_: unknown, data: { value: string }) => {
        const max = maxNonCaseCredit();
        const value = Number(data.value);
        setNonCaseCreditInput(!isNaN(value) && value > max ? String(max) : data.value);
    };

    const generateLastYearInMonths = () => {
        const months: Date[] = [];
        const today = new Date();

        for (let i = 6; i >= 0; i--) {
            const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
            months.push(date);
        }

        setBillMonths(months);
    };
    const getMonthOptionFormat = (date: Date) => {
        const dateFormatted = utils.date.formatDate(date, { month: 'long', year: 'numeric' }, true);
        const currentOrLast = utils.date.isLastMonth(date)
            ? ' (Last Month)'
            : utils.date.isCurrentMonth(date)
              ? ' (This Month)'
              : '';

        return `${dateFormatted} ${currentOrLast}`;
    };

    //Effects
    useEffect(() => {
        if (open) {
            generateLastYearInMonths();
            invoiceForm.resetForm({ values: initialFormValues() });
            setBillPeriodFilter(undefined);
            setPayTermsFilter(PayTerms.uponReceipt);
            setInstallments([]);
            messageDisplay.clearMessage?.();
            checkBillableRates();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, caseObj.caseId]);

    useEffect(() => {
        if (open) {
            setCaseCreditInput('');
            setNonCaseCreditInput('');
            loadCreditsAsync();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clientPartyId, caseObj.caseId]);

    useEffect(() => {
        if (!!billPeriodFilter) {
            const filterDate = billPeriodFilter == BillingPeriod.custom ? new Date() : new Date(billPeriodFilter);
            const firstDayOfMonth = new Date(filterDate.getFullYear(), filterDate.getMonth(), 1);
            const lastDayOfMonth = new Date(filterDate.getFullYear(), filterDate.getMonth() + 1, 0);

            invoiceForm.setFieldValue('billingPeriodStart', firstDayOfMonth);
            invoiceForm.setFieldValue('billingPeriodEnd', lastDayOfMonth);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [billPeriodFilter]);

    useEffect(() => {
        setDueDate(payTermsFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceForm.values.invoiceDate]);

    return (
        <Dialog open={open} onOpenChange={onDialogOpenChange} modalType='modal'>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                <DocumentAddRegular />
                            </Column>
                            <Column>Create New Invoice</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent className={mergeClasses(styles.content, commonStyles.scroll.classes.scrollbarClasses)}>
                        <Column gap='medium'>
                            <AppMessageDisplay messages={messageDisplay.messages} onDismiss={messageDisplay.removeMessage} />

                            <Row gap='large'>
                                {!caseObj?.isFixedFee && (
                                    <Column flex='2' gap='medium' padding='pl-3'>
                                        <Text size={400} italic weight='semibold'>
                                            Invoice Info
                                        </Text>
                                        <Column gap='medium' padding='pl-3'>
                                            <Field
                                                label='Invoice Number'
                                                required
                                                validationMessage={invoiceForm.errors.invoiceNumber}
                                            >
                                                <Row>
                                                    <Input
                                                        name='invoiceNumber'
                                                        value={invoiceForm.values.invoiceNumber}
                                                        onChange={invoiceForm.handleChange}
                                                        placeholder='Enter invoice number'
                                                        data-testid='create-invoice-dialog-number-input'
                                                    />
                                                    <Button
                                                        appearance='secondary'
                                                        onClick={onReGenerateInvoiceNumberClick}
                                                        data-testid='create-invoice-dialog-regenerate-number-button'
                                                    >
                                                        Re-Generate
                                                    </Button>
                                                </Row>
                                            </Field>
                                            <Field label='Invoice Date' required>
                                                <Row>
                                                    <DatePicker
                                                        name='invoiceDate'
                                                        value={invoiceForm.values.invoiceDate}
                                                        onSelectDate={onInvoiceDateSelect}
                                                        placeholder='Select invoice date'
                                                        minDate={
                                                            caseObj?.retainedDate
                                                                ? new Date(caseObj.retainedDate)
                                                                : undefined
                                                        }
                                                        data-testid='create-invoice-dialog-date-picker'
                                                    />
                                                    <Button
                                                        appearance='secondary'
                                                        onClick={onInvoiceDateTodayClick}
                                                        data-testid='create-invoice-dialog-today-button'
                                                    >
                                                        Today
                                                    </Button>
                                                </Row>
                                            </Field>
                                        </Column>
                                    </Column>
                                )}

                                <Column flex='3' gap='medium'>
                                    <Text size={400} italic weight='semibold'>
                                        {caseObj?.isFixedFee ? 'Installments' : 'Billing Period and Terms'}
                                    </Text>
                                    <Column gap='medium' padding='pl-3'>
                                        {!caseObj?.isFixedFee && (
                                            <Row noWrap gap='medium'>
                                                <Column width='auto'>
                                                    <Field label='Billing Period' style={{ alignSelf: 'start' }}>
                                                        <Row>
                                                            <Column>
                                                                <Dropdown
                                                                    onOptionSelect={onBillPeriodOptionSelect}
                                                                    data-testid='create-invoice-dialog-billing-period-dropdown'
                                                                >
                                                                    {billMonths.map((month: Date) => (
                                                                        <Option
                                                                            key={month.toDateString()}
                                                                            value={month.toDateString()}
                                                                            data-testid={`create-invoice-dialog-billing-period-option-${month.toDateString()}`}
                                                                        >
                                                                            {getMonthOptionFormat(month)}
                                                                        </Option>
                                                                    ))}
                                                                    <Option
                                                                        key={BillingPeriod.custom}
                                                                        value={BillingPeriod.custom}
                                                                        data-testid='create-invoice-dialog-billing-period-option-custom'
                                                                    >
                                                                        {BillingPeriod.custom}
                                                                    </Option>
                                                                </Dropdown>
                                                            </Column>
                                                            <Column vAlign='middle'>
                                                                {billPeriodFilter != BillingPeriod.custom && (
                                                                    <Text size={400} weight='semibold'>
                                                                        Billing Period:{' '}
                                                                        {utils.date.formatDate(
                                                                            invoiceForm.values.billingPeriodStart,
                                                                            { month: 'long', year: 'numeric' },
                                                                            true,
                                                                        )}
                                                                    </Text>
                                                                )}
                                                            </Column>
                                                        </Row>
                                                    </Field>
                                                </Column>
                                                <Column width='auto' vAlign='bottom'>
                                                    {billPeriodFilter == BillingPeriod.custom && (
                                                        <Field
                                                            label='Billing Period Start'
                                                            required
                                                            style={{ alignSelf: 'start' }}
                                                        >
                                                            <DatePicker
                                                                name='billingPeriodStart'
                                                                value={invoiceForm.values.billingPeriodStart}
                                                                onSelectDate={onBillingPeriodStartDateSelect}
                                                                placeholder='Select billing period start'
                                                                data-testid='create-invoice-dialog-billing-period-start-picker'
                                                            />
                                                        </Field>
                                                    )}
                                                </Column>
                                                <Column width='auto'>
                                                    {billPeriodFilter == BillingPeriod.custom && (
                                                        <Field
                                                            label='Billing Period End'
                                                            required
                                                            style={{ alignSelf: 'start' }}
                                                        >
                                                            <DatePicker
                                                                name='billingPeriodEnd'
                                                                value={invoiceForm.values.billingPeriodEnd}
                                                                onSelectDate={onBillingPeriodEndDateSelect}
                                                                minDate={invoiceForm.values.billingPeriodStart}
                                                                placeholder='Select billing period end'
                                                                data-testid='create-invoice-dialog-billing-period-end-picker'
                                                            />
                                                        </Field>
                                                    )}
                                                </Column>
                                            </Row>
                                        )}

                                        {caseObj?.isFixedFee && (
                                            <Column gap='small'>
                                                <Row justifyContent='space-between' noWrap>
                                                    <InfoLabel info={<>Fixed fee specified in the Case Info</>}>
                                                        <Text size={400} weight='semibold'>
                                                            Fixed Fee: {utils.formatCurrency(caseObj?.fixedAmount)}
                                                        </Text>
                                                    </InfoLabel>
                                                    <Text
                                                        size={300}
                                                        weight='semibold'
                                                        style={{
                                                            color: installmentsMatchFixedFee()
                                                                ? tokens.colorPaletteGreenForeground1
                                                                : tokens.colorPaletteRedForeground1,
                                                        }}
                                                    >
                                                        Installments Total: {utils.formatCurrency(installmentsTotal())} /{' '}
                                                        {utils.formatCurrency(caseObj?.fixedAmount)}
                                                    </Text>
                                                </Row>
                                                <Row justifyContent='space-between'>
                                                    <Text size={300} italic>
                                                        Add one or more installments. Each installment will be created as a
                                                        separate invoice.
                                                    </Text>
                                                    <Button
                                                        appearance='secondary'
                                                        icon={<AddRegular />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            addInstallment();
                                                        }}
                                                        data-testid='create-invoice-dialog-add-installment-button'
                                                    >
                                                        Add Installment
                                                    </Button>
                                                </Row>
                                                {installments.map((inst, idx) => (
                                                    <Row key={inst.id} gap='medium' noWrap>
                                                        <Column width='auto'>
                                                            <Field label={idx === 0 ? 'Invoice Number' : undefined} required>
                                                                <Row>
                                                                    <Input
                                                                        value={inst.invoiceNumber}
                                                                        onChange={(_, data) =>
                                                                            updateInstallment(inst.id, {
                                                                                invoiceNumber: data.value,
                                                                            })
                                                                        }
                                                                        placeholder='Invoice number'
                                                                        data-testid={`create-invoice-dialog-installment-number-${idx}`}
                                                                    />
                                                                    <Button
                                                                        appearance='secondary'
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            regenerateInstallmentNumber(inst.id);
                                                                        }}
                                                                        data-testid={`create-invoice-dialog-installment-regenerate-${idx}`}
                                                                    >
                                                                        Re-Generate
                                                                    </Button>
                                                                </Row>
                                                            </Field>
                                                        </Column>
                                                        <Column width='auto'>
                                                            <Field label={idx === 0 ? 'Invoice Date' : undefined} required>
                                                                <DatePicker
                                                                    value={inst.invoiceDate}
                                                                    onSelectDate={(date) =>
                                                                        updateInstallment(inst.id, {
                                                                            invoiceDate: date || new Date(),
                                                                        })
                                                                    }
                                                                    placeholder='Select invoice date'
                                                                    minDate={
                                                                        caseObj?.retainedDate
                                                                            ? new Date(caseObj.retainedDate)
                                                                            : undefined
                                                                    }
                                                                    data-testid={`create-invoice-dialog-installment-date-${idx}`}
                                                                />
                                                            </Field>
                                                        </Column>
                                                        <Column width='auto'>
                                                            <Field label={idx === 0 ? 'Amount' : undefined} required>
                                                                <Input
                                                                    type='number'
                                                                    value={inst.amount}
                                                                    onChange={(_, data) =>
                                                                        updateInstallment(inst.id, {
                                                                            amount: data.value,
                                                                        })
                                                                    }
                                                                    placeholder='0.00'
                                                                    data-testid={`create-invoice-dialog-installment-amount-${idx}`}
                                                                />
                                                            </Field>
                                                        </Column>
                                                        <Column width='auto'>
                                                            <Field label={idx === 0 ? 'Payment Terms' : undefined} required>
                                                                <Dropdown
                                                                    style={{ minWidth: '150px' }}
                                                                    value={inst.paymentTerms}
                                                                    selectedOptions={[inst.paymentTerms]}
                                                                    onOptionSelect={(e, data) => {
                                                                        e?.stopPropagation?.();
                                                                        updateInstallment(inst.id, {
                                                                            paymentTerms:
                                                                                data.optionText ?? PayTerms.uponReceipt,
                                                                        });
                                                                    }}
                                                                    data-testid={`create-invoice-dialog-installment-terms-${idx}`}
                                                                >
                                                                    {Object.values(PayTerms).map((option) => (
                                                                        <Option key={option} value={option}>
                                                                            {option}
                                                                        </Option>
                                                                    ))}
                                                                </Dropdown>
                                                            </Field>
                                                        </Column>
                                                        <Column width='auto' vAlign='bottom'>
                                                            <Button
                                                                appearance='subtle'
                                                                icon={<DeleteRegular />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeInstallment(inst.id);
                                                                }}
                                                                aria-label='Remove installment'
                                                                data-testid={`create-invoice-dialog-installment-remove-${idx}`}
                                                            />
                                                        </Column>
                                                    </Row>
                                                ))}
                                            </Column>
                                        )}

                                        {!caseObj?.isFixedFee && (
                                            <Row>
                                                <Column width='auto' vAlign='middle'>
                                                    <Field label='Payment Terms' style={{ alignSelf: 'start' }}>
                                                        <Dropdown
                                                            style={{ minWidth: '150px' }}
                                                            value={payTermsFilter}
                                                            onOptionSelect={onPayTermsChange}
                                                            data-testid='create-invoice-dialog-payment-terms-dropdown'
                                                        >
                                                            {Object.values(PayTerms).map((option) => (
                                                                <Option
                                                                    key={option}
                                                                    value={option}
                                                                    data-testid={`create-invoice-dialog-payment-terms-option-${option}`}
                                                                >
                                                                    {option}
                                                                </Option>
                                                            ))}
                                                        </Dropdown>
                                                    </Field>
                                                </Column>
                                                <Column width='auto' vAlign='middle'>
                                                    <Field
                                                        label='Due Date'
                                                        required={
                                                            caseEngagementService?.engagementDoc?.paymentBy ==
                                                            PayTerms.custom
                                                        }
                                                    >
                                                        {payTermsFilter == PayTerms.custom ? (
                                                            <DatePicker
                                                                name='dueDate'
                                                                value={invoiceForm.values.dueDate}
                                                                onSelectDate={onDueDateSelect}
                                                                placeholder='Select due date'
                                                                readOnly={
                                                                    caseEngagementService?.engagementDoc?.paymentBy !=
                                                                    PayTerms.custom
                                                                }
                                                                onClick={onDueDateClick}
                                                                data-testid='create-invoice-dialog-due-date-picker'
                                                            />
                                                        ) : (
                                                            <Text size={400} weight='semibold'>
                                                                {utils.date.formatDate(
                                                                    invoiceForm.values.dueDate,
                                                                    {
                                                                        weekday: 'short',
                                                                        month: 'short',
                                                                        day: '2-digit',
                                                                        year: 'numeric',
                                                                    },
                                                                    true,
                                                                )}
                                                            </Text>
                                                        )}
                                                    </Field>
                                                </Column>
                                            </Row>
                                        )}

                                        <Field label='Case Engagement'>
                                            <Checkbox
                                                checked={invoiceForm.values.attachEngagement}
                                                onChange={onAttachEngagementChange}
                                                label='Attach Engagement Document'
                                                data-testid='create-invoice-dialog-attach-engagement-checkbox'
                                            />
                                        </Field>
                                    </Column>
                                </Column>
                            </Row>

                            {showCredits() && (
                                <Column
                                    padding='px-2 py-2'
                                    borderRadius='8px'
                                    subtleBorder
                                    minHeight='fit-content'
                                    gap='medium'
                                >
                                    <Row gap='small' noWrap>
                                        <Column width='auto' vAlign='middle'>
                                            <Money20Regular />
                                        </Column>
                                        <Column vAlign='middle'>
                                            <Text size={400} italic weight='semibold'>
                                                Available Credits
                                            </Text>
                                        </Column>
                                    </Row>

                                    {creditsLoading ? (
                                        <Spinner size='tiny' label='Loading credits...' />
                                    ) : (
                                        <Row gap='medium' flexWrap='wrap-reverse' padding='pl-3'>
                                            <Column flex='1 1 32rem' minWidth='20rem'>
                                                <Row gap='medium' flexWrap='wrap' align='left'>
                                                    {/* <Column flex='1' className={styles.creditCard} gap='small'>
                                                        <Text size={200} className={styles.creditLabel}>
                                                            Total Available
                                                        </Text>
                                                        <Text
                                                            className={styles.creditValue}
                                                            data-testid='create-invoice-dialog-total-credit-available'
                                                        >
                                                            {utils.formatCurrency(totalCreditAvailable())}
                                                        </Text>
                                                    </Column> */}
                                                    <Column flex='1' className={styles.creditCard} gap='small'>
                                                        <Text size={200} className={styles.creditLabel}>
                                                            Case Specific
                                                        </Text>
                                                        <Text
                                                            className={styles.creditValue}
                                                            data-testid='create-invoice-dialog-case-credit-available'
                                                        >
                                                            {utils.formatCurrency(caseCreditAvailable())}
                                                        </Text>
                                                        <Field
                                                            label='Apply to Invoice'
                                                            hint={
                                                                isCaseCreditLocked()
                                                                    ? 'Applied automatically by account settings'
                                                                    : `Up to ${utils.formatCurrency(maxCaseCredit())}`
                                                            }
                                                        >
                                                            <Input
                                                                className={styles.creditInput}
                                                                type='number'
                                                                min={0}
                                                                max={maxCaseCredit()}
                                                                contentBefore='$'
                                                                placeholder='0.00'
                                                                value={
                                                                    isCaseCreditLocked()
                                                                        ? appliedCaseCredit().toFixed(2)
                                                                        : caseCreditInput
                                                                }
                                                                onChange={onCaseCreditChange}
                                                                disabled={isCaseCreditLocked() || maxCaseCredit() <= 0}
                                                                data-testid='create-invoice-dialog-case-credit-input'
                                                            />
                                                        </Field>
                                                    </Column>
                                                    <Column flex='1' className={styles.creditCard} gap='small'>
                                                        <Text size={200} className={styles.creditLabel}>
                                                            Non-Case Specific
                                                        </Text>
                                                        <Text
                                                            className={styles.creditValue}
                                                            data-testid='create-invoice-dialog-non-case-credit-available'
                                                        >
                                                            {utils.formatCurrency(nonCaseCreditAvailable())}
                                                        </Text>
                                                        <Field
                                                            label='Apply to Invoice'
                                                            hint={
                                                                isNonCaseCreditLocked()
                                                                    ? 'Applied automatically by account settings'
                                                                    : `Up to ${utils.formatCurrency(maxNonCaseCredit())}`
                                                            }
                                                        >
                                                            <Input
                                                                className={styles.creditInput}
                                                                type='number'
                                                                min={0}
                                                                max={maxNonCaseCredit()}
                                                                contentBefore='$'
                                                                placeholder='0.00'
                                                                value={
                                                                    isNonCaseCreditLocked()
                                                                        ? appliedNonCaseCredit().toFixed(2)
                                                                        : nonCaseCreditInput
                                                                }
                                                                onChange={onNonCaseCreditChange}
                                                                disabled={isNonCaseCreditLocked() || maxNonCaseCredit() <= 0}
                                                                data-testid='create-invoice-dialog-non-case-credit-input'
                                                            />
                                                        </Field>
                                                    </Column>
                                                </Row>
                                            </Column>

                                            <Column flex='1 1 16rem' gap='small' vAlign='top' className={styles.creditCard}>
                                                <Row gap='medium' noWrap justifyContent='space-between'>
                                                    <Column width='auto'>
                                                        <Text size={300} className={styles.creditLabel}>
                                                            Invoice Total
                                                        </Text>
                                                    </Column>
                                                    <Column width='auto'>
                                                        <Text size={300} weight='semibold'>
                                                            {utils.formatCurrency(invoiceTotal())}
                                                        </Text>
                                                    </Column>
                                                </Row>
                                                <Row gap='medium' noWrap justifyContent='space-between'>
                                                    <Column width='auto'>
                                                        <Text size={300} className={styles.creditLabel}>
                                                            Credits Applied
                                                        </Text>
                                                    </Column>
                                                    <Column width='auto'>
                                                        <Text
                                                            size={300}
                                                            weight='semibold'
                                                            style={{ color: tokens.colorPaletteGreenForeground1 }}
                                                            data-testid='create-invoice-dialog-credits-applied'
                                                        >
                                                            -{utils.formatCurrency(totalCreditApplied())}
                                                        </Text>
                                                    </Column>
                                                </Row>
                                                <Row gap='medium' noWrap justifyContent='space-between'>
                                                    <Column width='auto'>
                                                        <Text size={400} weight='semibold'>
                                                            Balance Due
                                                        </Text>
                                                    </Column>
                                                    <Column width='auto'>
                                                        <Text
                                                            size={400}
                                                            weight='semibold'
                                                            data-testid='create-invoice-dialog-balance-due'
                                                        >
                                                            {utils.formatCurrency(balanceDue())}
                                                        </Text>
                                                    </Column>
                                                </Row>
                                            </Column>
                                        </Row>
                                    )}
                                </Column>
                            )}

                            {!caseObj?.isFixedFee && (
                                <Column padding='px-2 py-2' borderRadius='8px' subtleBorder minHeight='fit-content'>
                                    <Text size={400} italic weight='semibold'>
                                        Billable Time
                                    </Text>
                                    <InvoiceBillableTimeEntryTable
                                        entries={timeTrackingService?.billableTimeEntries?.filter(filterTimeEntries)}
                                        assignedUsers={caseObj.assignedUsers}
                                    />
                                </Column>
                            )}

                            {!caseObj?.isFixedFee && canSeeExpenses() && (
                                <Column padding='px-2 py-2' borderRadius='8px' subtleBorder minHeight='fit-content'>
                                    <Text size={400} italic weight='semibold'>
                                        Expenses
                                    </Text>
                                    <InvoiceExpensesTable expenses={expenseService?.expenses?.filter(filterExpenses)} />
                                </Column>
                            )}
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            className={commonStyles.button.root}
                            icon={<SaveRegular />}
                            appearance='primary'
                            onClick={onCreateInvoiceClick}
                            disabled={!canCreateInvoice()}
                            data-testid='create-invoice-dialog-submit-button'
                        >
                            {isCreating ? 'Creating...' : 'Create Invoice'}
                        </Button>
                        <Button
                            appearance='secondary'
                            onClick={onCancelClick}
                            disabled={isCreating}
                            data-testid='create-invoice-dialog-cancel-button'
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
